/**
 * UI Preferences Context
 * Holds per-user appearance (theme, mode, font size). Bootstrap from storage for first paint;
 * when authenticated we always fetch GET /users/me/ui-preferences so backend is runtime authority.
 * Updated via PATCH from Appearance settings. Cleared on logout.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { tokenManager } from '@/utils/tokenManager'

/** Guards initial fetch so React Strict Mode (double-invoke of effects) does not trigger two GET /users/me/ui-preferences on reload. */
let initialUIPreferencesFetchInProgress = false
import {
  getUIPreferences,
  patchUIPreferences,
  type UIPreferences,
  type PatchUIPreferencesInput,
} from '@/services/api/shared/ui-preferences.service'
import {
  getStoredUIPreferences,
  setStoredUIPreferences,
  clearStoredUIPreferences,
} from '@/utils/ui-preferences-storage.util'

interface UIPreferencesContextValue {
  uiPreferences: UIPreferences | null
  isLoading: boolean
  error: Error | null
  setUIPreferences: (prefs: UIPreferences | null) => void
  refetch: () => Promise<void>
  updateUIPreferences: (input: PatchUIPreferencesInput) => Promise<UIPreferences>
}

const UIPreferencesContext = createContext<UIPreferencesContextValue | undefined>(undefined)

function bootstrapUIPreferencesFromStorage(): UIPreferences | null {
  // Do not hydrate appearance from localStorage on public / logged-out shells — avoids themed inputs on /login.
  if (!tokenManager.hasToken('contextToken')) return null
  return getStoredUIPreferences()
}

export function UIPreferencesProvider({ children }: { children: ReactNode }) {
  const [uiPreferences, setUIPreferencesState] = useState<UIPreferences | null>(bootstrapUIPreferencesFromStorage)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const fetchPreferences = useCallback(async () => {
    if (!tokenManager.hasToken('contextToken') && !tokenManager.hasToken('accessToken')) {
      setUIPreferencesState(null)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const data = await getUIPreferences()
      setUIPreferencesState(data)
      setStoredUIPreferences(data)
    } catch (e) {
      // Handle 401 errors gracefully - don't show error, just use stored preferences
      // The axios interceptor will handle token refresh and recovery
      const is401 = (e as any)?.response?.status === 401
      if (is401) {
        // Keep stored preferences if available, don't set error
        const stored = getStoredUIPreferences()
        if (stored) {
          setUIPreferencesState(stored)
        }
      } else {
        setError(e instanceof Error ? e : new Error('Failed to load UI preferences'))
        setUIPreferencesState(null)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const setUIPreferences = useCallback((prefs: UIPreferences | null) => {
    setUIPreferencesState(prefs)
    if (prefs) setStoredUIPreferences(prefs)
    else clearStoredUIPreferences()
  }, [])

  // When user updates prefs from settings, invalidate cache and persist new prefs (state + localStorage)
  const updateUIPreferences = useCallback(async (input: PatchUIPreferencesInput) => {
    const data = await patchUIPreferences(input)
    setUIPreferencesState(data)
    setStoredUIPreferences(data)
    return data
  }, [])

  // On load: bootstrap from storage for first paint; when authenticated, ALWAYS fetch so backend is runtime authority.
  // Guard: React Strict Mode double-invokes effects on mount; avoid two GET /users/me/ui-preferences on reload.
  useEffect(() => {
    if (!tokenManager.hasToken('contextToken') && !tokenManager.hasToken('accessToken')) {
      return
    }
    if (initialUIPreferencesFetchInProgress) {
      return
    }
    initialUIPreferencesFetchInProgress = true
    fetchPreferences().finally(() => {
      initialUIPreferencesFetchInProgress = false
    })
  }, [fetchPreferences])

  const value: UIPreferencesContextValue = {
    uiPreferences,
    isLoading,
    error,
    setUIPreferences,
    refetch: fetchPreferences,
    updateUIPreferences,
  }

  return (
    <UIPreferencesContext.Provider value={value}>
      {children}
    </UIPreferencesContext.Provider>
  )
}

export function useUIPreferences(): UIPreferencesContextValue {
  const ctx = useContext(UIPreferencesContext)
  if (ctx === undefined) {
    throw new Error('useUIPreferences must be used within UIPreferencesProvider')
  }
  return ctx
}
